// CompactDropdown: a small "Label: Value ▾" trigger that opens a popover menu of choices (DESIGN §2).
// Used for the Workspace client filter, Overview period, Performance scopes, inline Owner pickers.
import './compact-dropdown.css';
import { Icon } from '../icons.jsx';
import { MenuList, Popover, usePopover } from './popover.jsx';
import { cx } from './ui.jsx';

const toOption = (o) => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) });

// options: strings or [{ value, label, meta?, icon?, avatar? }]. onChange(value).
// label: optional prefix shown in the trigger ("Client:"). valueLabel: overrides the trigger text.
// extra: menu items appended after a divider (e.g. [{ label: 'New client', icon: 'plus', onSelect }]).
// variant: 'ghost' (default, text-like) | 'bordered'. size: 'sm' (30px) | 'md' (34px).
export function CompactDropdown({
  label, value, options = [], onChange, extra, valueLabel, placeholder = 'Choose…', placement = 'bottom-start',
  width = 240, variant = 'ghost', size = 'md', disabled, ariaLabel, class: cls,
}) {
  const pop = usePopover();
  const opts = options.map(toOption);
  const selected = opts.find((o) => o.value === value);
  const shown = valueLabel ?? selected?.label ?? placeholder;
  const items = opts.map((o) => ({
    label: o.label,
    meta: o.meta,
    icon: o.icon,
    avatar: o.avatar,
    checked: o.value === value,
    disabled: o.disabled,
    onSelect: () => { if (o.value !== value) onChange?.(o.value); },
  }));
  if (extra?.length) items.push({ divider: true }, ...extra);
  return (
    <>
      <button
        type="button"
        ref={pop.anchorRef}
        class={cx('dropdown', `dropdown-${variant}`, size === 'sm' && 'dropdown-sm', cls)}
        aria-label={ariaLabel || (label ? `${label.replace(/:$/, '')}: ${shown}` : undefined)}
        disabled={disabled}
        {...pop.triggerProps}
      >
        {label && <span class="dropdown-label">{label}</span>}
        <span class="dropdown-value">{shown}</span>
        <Icon name="chevronDown" size={14} class="dropdown-chevron" />
      </button>
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} placement={placement} width={width} role="menu" label={label || ariaLabel}>
        <MenuList items={items} onClose={pop.close} />
      </Popover>
    </>
  );
}
